import { useEffect, useState } from 'react';
import {
  EPSPeakCalculation,
  FPMPeakCalculation,
} from '../services/peakCalculation';
import { useCurrencyWithSymbol } from 'src/common/hooks/widgets/useCurrencyWithSymbol';
import { EPS_QTY, FPM_QTY, Price } from '../constants/prices';
import {
  SPREAD20,
  ONPREM_DISCOUNT,
  ENTERPRISE_SIEM_RU_RATIO,
  ENTERPRISE_NDR_RU_RATIO,
  USAGE_SIEM_RU_RATIO,
  USAGE_NDR_RU_RATIO,
} from '../constants/spread';
import { getListPrice } from 'src/common/hooks/widgets/fetchData';

/**
 * Calculate the estimated price
 *
 * @param {object} intl used for the formatting
 * @param {string} lang the language code to get the currency sign for
 * @param {number} employees : Number of employees/workstations
 * @param {number} servers : Number of servers
 * @param {boolean} ndr : Includes NDR
 * @param {boolean} OnPrem : Includes OnPrem
 * @returns {Array}
 */

export interface PropsFunc {
  langCode: string;
  currency: string;
  localeCode: string;
  country: string;
  employees: number;
  servers: number;
  ndr: boolean;
  onPrem: boolean;
}

interface APIBody {
  currency: string;
  countryCode3: string;
  parts: PartsToQuery[];
}

interface PartsToQuery {
  partNumber: string;
  partType: string;
}

const partsToQuery = [
  {
    partNumber: 'D0A7SZX',
  },
  {
    partNumber: 'D0A7TZX',
  },
  {
    partNumber: 'D0AE7ZX',
  },
];

interface PriceParts {
  partNumber: string;
  pricingIndicator: string;
  pricingModel: number;
  scaleQtyPrice: ScaleQtyPrice[];
}

interface ScaleQtyPrice {
  qty: number;
  price: number;
}

export interface IFetchError {
  status: number;
  statusText: string;
}

export const usePrice = ({
  currency,
  localeCode,
  country,
  employees,
  servers,
  ndr,
  onPrem,
}: PropsFunc) => {
  const [lowerRaw, setLowerRaw] = useState(0);
  const [higherRaw, setHigherRaw] = useState(0);
  const [higherPrice, setHigherPrice] = useCurrencyWithSymbol(
    currency,
    localeCode,
    0,
  );
  const [lowerPrice, setLowerPrice] = useCurrencyWithSymbol(
    currency,
    localeCode,
    0,
  );

  const [fpmPrices, setFpmPrices] = useState([]);
  const [epsPrices, setEpsPrices] = useState([]);
  const [ruMonthlySublistPrice, setRuMonthlySublistPrice] = useState(0);
  const [dataFetched, setDataFetched] = useState([]);
  const [fetchingError, setFetchingError] = useState({});

  const setPricesData = (products: PriceParts[]) => {
    if (products?.length) {
      const EPS_PRICE = products.find(
        (i: PriceParts) => i.partNumber == 'D0A7SZX',
      );
      const EPS_PRICE_ONE = EPS_PRICE?.scaleQtyPrice.find(
        (i: ScaleQtyPrice) => i.qty == 10,
      )?.price;
      const EPS_PRICE_TWO = EPS_PRICE?.scaleQtyPrice.find(
        (i: ScaleQtyPrice) => i.qty == 50,
      )?.price;
      const EPS_PRICE_THREE = EPS_PRICE?.scaleQtyPrice.find(
        (i: ScaleQtyPrice) => i.qty == 250,
      )?.price;

      const FPM_PRICE = products.find(
        (i: PriceParts) => i.partNumber == 'D0A7TZX',
      );
      const FPM_PRICE_ONE = FPM_PRICE?.scaleQtyPrice.find(
        (i: ScaleQtyPrice) => i.qty == 10,
      )?.price;
      const FPM_PRICE_TWO = FPM_PRICE?.scaleQtyPrice.find(
        (i: ScaleQtyPrice) => i.qty == 100,
      )?.price;
      const FPM_PRICE_THREE = FPM_PRICE?.scaleQtyPrice.find(
        (i: ScaleQtyPrice) => i.qty == 250,
      )?.price;

      const EPS_PRICES: any = [EPS_PRICE_ONE, EPS_PRICE_TWO, EPS_PRICE_THREE];
      setEpsPrices(
        EPS_PRICES.map((price: number) => ({
          price: price ? Math.round(price * (1 - ONPREM_DISCOUNT)) : 0,
        })),
      );
      const FPM_PRICES: any = [FPM_PRICE_ONE, FPM_PRICE_TWO, FPM_PRICE_THREE];
      setFpmPrices(
        FPM_PRICES.map((price: number) => ({
          price: price ? Math.round(price * (1 - ONPREM_DISCOUNT)) : 0,
        })),
      );

      const PART_NUM_PRICE = products.find(
        (i: PriceParts) => i.partNumber == 'D0AE7ZX',
      );
      setRuMonthlySublistPrice(
        PART_NUM_PRICE?.scaleQtyPrice?.length == 1
          ? PART_NUM_PRICE.scaleQtyPrice[0].price
          : 0,
      );
    }
  };

  /**
   *
   *  @done Update 4.4.2022 >  setting a minimum of 3 paks for on prem ( [Enterprise SIEM Paks] and [Usage SIEM Paks] must be minimum 3)
   *
   * Calculate the final on Prem price
   *
   * @param {number} peakEPS : EPS peak
   * @param {number} peakFPM : FPM peak
   * @param {boolean} isLower : lowest or highest price range sought
   * @returns {number}
   */
  const calculatedPriceOnPrem = (
    peakEPS: number,
    peakFPM: number,
    isLower: boolean,
  ): number => {
    const scale = isLower ? 0.8 : 1.2;

    const usageSIEM_2 = Math.ceil((peakEPS * USAGE_SIEM_RU_RATIO) / 100);
    const enterpriseSIEM_2 = Math.ceil(
      (servers * ENTERPRISE_SIEM_RU_RATIO) / 100,
    );

    let usageSIEM = (peakEPS * USAGE_SIEM_RU_RATIO) / 100;
    let enterpriseSIEM = (servers * ENTERPRISE_SIEM_RU_RATIO) / 100;
    let calculatedPrice = 0;

    const minPaks = (paks = 0) => (paks > 3 ? paks : 3);

    if (ndr) {
      const usageFull = minPaks(
        Math.ceil(
          Math.ceil((peakFPM * USAGE_NDR_RU_RATIO) / 100 + usageSIEM) * scale,
        ),
      );
      const enterpriseFull = minPaks(
        Math.ceil(
          Math.ceil(
            (servers * ENTERPRISE_NDR_RU_RATIO) / 100 + enterpriseSIEM,
          ) * scale,
        ),
      );

      calculatedPrice = usageFull < enterpriseFull ? usageFull : enterpriseFull;
    } else {
      usageSIEM = minPaks(Math.ceil(usageSIEM_2 * scale));
      enterpriseSIEM = minPaks(Math.ceil(enterpriseSIEM_2 * scale));

      calculatedPrice = usageSIEM < enterpriseSIEM ? usageSIEM : enterpriseSIEM;
    }
    return (
      Math.round(
        (calculatedPrice * ruMonthlySublistPrice * (1 - ONPREM_DISCOUNT)) / 10,
      ) * 10
    );
  };

  /**
   * Calculate the final price according to quantity and price per tier
   *
   * @param {number} peak : EPS or FPM peak
   * @param {Price[]} PRICES : Array of prices per tier
   * @param {number[]} QUANTITIES : Array of quantities per tier
   * @returns {number}
   */
  const calculatedPrice = (
    peak: number,
    PRICES: Price[],
    QUANTITIES: number[],
  ): number => {
    const [PRICE1, PRICE2, PRICE3] = PRICES; //ex u-en [520, 130, 117]
    const [QTY_START, QTY2, QTY3, QTY4] = QUANTITIES; //ex: us-en 4 10 50 250
    let price;
    let newPeak;
    switch (true) {
      // Is it in T1 - 10 ?
      case peak < QTY2:
        newPeak = peak > 0 && peak < QTY_START ? QTY_START : peak;
        price = newPeak * PRICE1?.price;
        break;
      // Is it in T2  - 50 ?
      case peak < QTY3:
        newPeak = peak - QTY2;
        price = QTY2 * PRICE1?.price + newPeak * PRICE2?.price;
        break;
      //Is it in T3 - 250 ?
      case peak < QTY4:
        newPeak = peak - QTY3;
        price =
          QTY2 * PRICE1?.price +
          (QTY3 - QTY2) * PRICE2?.price +
          newPeak * PRICE3?.price;
        break;
      default:
        newPeak = peak > 0 && peak < QTY_START ? QTY_START : peak;
        price = newPeak * PRICE1?.price;
        break;
    }

    return price;
  };

  /**
   * Calculate the estimated price (peak EPS + peak FPM)
   *
   * @param {number} spread : Spread for range estimates
   * @param {boolean} isLower : lowest or highest price range sought
   * @param {Price[]} epsPrices : Array of eps prices
   * @param {Price[]} fpmPrices : Array of fps prices
   * @returns {number}
   */
  const estimatedPrice = (
    spread: number,
    isLower: boolean,
    epsPrices: Price[],
    fpmPrices: Price[],
  ): number => {
    const peakEPS = EPSPeakCalculation(employees, servers);
    const peakFPM = FPMPeakCalculation(employees, servers);

    // on Prem
    if (onPrem) {
      return calculatedPriceOnPrem(peakEPS, peakFPM, isLower);
    }

    // on Cloud
    if (!onPrem) {
      if (ndr) {
        return (
          calculatedPrice(Math.ceil(peakEPS * spread), epsPrices, EPS_QTY) +
          calculatedPrice(Math.ceil(peakFPM * spread), fpmPrices, FPM_QTY)
        );
      } else {
        return calculatedPrice(Math.ceil(peakEPS * spread), epsPrices, EPS_QTY);
      }
    }
    return 0;
  };

  /**
   * Calculate the OnCloud Initial estimated price (peak EPS + peak FPM)
   *
   * @param {number} spread : Spread for range estimates
   * @param {boolean} isLower : lowest or highest price range sought
   * @returns {number}
   */
  const estimatedOnCloudInitialPrice = (
    spread: number,
    isLower: boolean,
    epsPrices: Price[],
  ): number => {
    const peakEPS = EPSPeakCalculation(1, 1);
    return calculatedPrice(Math.ceil(peakEPS * spread), epsPrices, EPS_QTY);
  };

  const fetchPrices = () => {
    const body: any = {
      currency: currency,
      countryCode3: country,
      parts: partsToQuery,
    };
    getListPrice(
      `https://api.marketplace.ibm.com/purchase/catalog/resources/price/info`,
      body,
      'POST',
    )
      .then((data) => {
        if (data.parts && data.parts.length !== 0) {
          setPricesData(data.parts);
          setDataFetched(data.parts);
        } else {
          console.log('Else Part');
          setDataFetched([]);
          setFetchingError({});
        }
      })
      .catch((error) => {
        setDataFetched([]);
        setFetchingError(error.response);
        if (error.response) {
          // Request made but the server responded with an error
          console.log(error.response.data);
          console.log(error.response.status); // 404
          if (error.response.status === 404)
            console.log('price source: backup');
        } else if (error.request) {
          // Request made but no response is received from the server.
          console.log(error.request);
        } else {
          // Error occured while setting up the request
          console.log('Error', error.message);
        }
      });
  };

  useEffect(() => {
    if (currency && country && dataFetched.length === 0) {
      fetchPrices();
    }
  }, [localeCode, currency]);

  useEffect(() => {
    // After 3 seconds whithout response of the api it will display the values of the backupAPI
    setTimeout(() => {
      if (dataFetched.length === 0) {
        setPricesData([]);
      }
    }, 3000);
  }, [dataFetched]);

  useEffect(() => {
    const lower = estimatedPrice(1 - SPREAD20, true, epsPrices, fpmPrices);
    const higher = estimatedPrice(1 + SPREAD20, false, epsPrices, fpmPrices);

    if (lower === higher) {
      setLowerPrice(higher);
      setLowerRaw(higher);
      setHigherPrice(higher * 1.2);
      setHigherRaw(higher * 1.2);
    } else {
      setLowerPrice(lower);
      setLowerRaw(higher);
      setHigherPrice(higher);
      setHigherRaw(higher);
    }
  }, [employees, servers, ndr, onPrem, ruMonthlySublistPrice]);

  return [
    lowerPrice,
    higherPrice,
    fetchingError,
    lowerRaw,
    higherRaw,
    fpmPrices,
    epsPrices,
  ];
};
